"use client"

import { useTheme } from "next-themes"
import { Toaster as Sonner, type ToasterProps } from "sonner"
import { IconCircleCheck, IconInfoCircle, IconAlertTriangle, IconAlertOctagon, IconLoader } from "@tabler/icons-react"
import { cn } from "@/lib/utils"

const defaultToastClass =
  "border border-border bg-background text-foreground shadow-lg"

function Toaster({
  position = "bottom-left",
  closeButton = true,
  toastOptions: toastOptionsProp,
  ...rest
}: ToasterProps) {
  const { theme = "system" } = useTheme()
  const baseClassNames = toastOptionsProp?.classNames

  return (
    <Sonner
      position={position}
      closeButton={closeButton}
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      icons={{
        success: (
          <IconCircleCheck className="size-4" />
        ),
        info: (
          <IconInfoCircle className="size-4" />
        ),
        warning: (
          <IconAlertTriangle className="size-4" />
        ),
        error: (
          <IconAlertOctagon className="size-4" />
        ),
        loading: (
          <IconLoader className="size-4 animate-spin" />
        ),
      }}
      style={
        {
          "--normal-bg": "var(--background)",
          "--normal-text": "var(--foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
        } as React.CSSProperties
      }
      toastOptions={{
        ...toastOptionsProp,
        classNames: {
          toast: cn(defaultToastClass, baseClassNames?.toast),
          title: cn("text-foreground", baseClassNames?.title),
          description: cn("text-muted-foreground", baseClassNames?.description),
          success: cn(defaultToastClass, baseClassNames?.success),
          info: cn(defaultToastClass, baseClassNames?.info),
          warning: cn(defaultToastClass, baseClassNames?.warning),
          loading: cn(defaultToastClass, baseClassNames?.loading),
          error: cn(
            "border border-destructive/30 bg-destructive/10 text-destructive",
            baseClassNames?.error,
          ),
        },
      }}
      {...rest}
    />
  )
}

export { Toaster }
